import { z } from "zod";
import { cleanupNonNestedPath, isNotNestedPath, type TypedSchema, type TypedSchemaError } from "vee-validate";

/*
 * Zod 4 → VeeValidate 4 adapter.
 *
 * The official `@vee-validate/zod` package only supports Zod 3 (peer `zod@^3.24`, and it relies on Zod 3
 * internals like `ZodFirstPartyTypeKind` / `_def.typeName`). VeeValidate 5 will accept Zod 4 directly via
 * Standard Schema, but it is still in beta. Until then, this is a port of `@vee-validate/zod`'s
 * `toTypedSchema` to the Zod 4 API.
 *
 * When VeeValidate 5 is stable: drop this file and pass the Zod schemas straight to `useForm`/`useField`.
 */

/** `true` for numeric path segments such as the `0` in `items.0.name` */
const isIndex = (segment: PropertyKey): boolean => typeof segment === "number" || Number(segment) >= 0;

/**
 * Converts a Zod issue path (`['items', 0, 'name']`) into VeeValidate's path syntax (`items[0].name`).
 * @param path Zod issue path
 */
function toFormPath(path: readonly PropertyKey[]): string {
  return path.reduce<string>((fullPath, segment, index) => {
    if (index === 0) return String(segment);
    return isIndex(segment) ? `${fullPath}[${String(segment)}]` : `${fullPath}.${String(segment)}`;
  }, "");
}

/**
 * Groups Zod issues by form path. Union errors are flattened so every branch's messages end up on the
 * field they belong to.
 * @param issues Zod issues of a failed parse
 * @param errors accumulator keyed by form path
 */
function collectIssues(issues: readonly z.core.$ZodIssue[], errors: Record<string, TypedSchemaError>): void {
  for (const issue of issues) {
    const path = toFormPath(issue.path);
    if (issue.code === "invalid_union") {
      collectIssues(issue.errors.flat(), errors);
      if (!path) continue;
    }
    errors[path] ??= { path, errors: [] };
    errors[path].errors.push(issue.message);
  }
}

/**
 * Resolves the sub-schema at a VeeValidate field path, e.g. `contacts[0].email`.
 * Optional/nullable/default wrappers are looked through. Returns `null` if the path doesn't exist.
 * @param path VeeValidate field path
 * @param schema root schema
 */
function getSchemaForPath(path: string, schema: z.ZodType): z.ZodType | null {
  const unwrap = (s: z.ZodType): z.ZodType =>
    s instanceof z.ZodOptional || s instanceof z.ZodNullable || s instanceof z.ZodDefault
      ? unwrap(s.unwrap() as z.ZodType)
      : s;

  const root = unwrap(schema);
  if (!(root instanceof z.ZodObject)) return null;
  if (isNotNestedPath(path)) return (root.shape[cleanupNonNestedPath(path)] as z.ZodType | undefined) ?? null;

  let current: z.ZodType | null = root;
  for (const segment of path.split(/\.|\[(\d+)\]/).filter(Boolean)) {
    if (!current) return null;
    const unwrapped = unwrap(current);
    if (unwrapped instanceof z.ZodObject) {
      current = (unwrapped.shape[segment] as z.ZodType | undefined) ?? null;
    } else if (isIndex(segment) && unwrapped instanceof z.ZodArray) {
      current = unwrapped.element as z.ZodType;
    } else {
      return null;
    }
  }
  return current;
}

/**
 * Wraps a Zod 4 schema so it can be used as `validationSchema` in `useForm()` / `<Form>`, or as the rules of
 * a single `useField()` / `<Field>`.
 *
 * @example
 * const { handleSubmit, meta } = useForm({
 *   validationSchema: toTypedSchema(z.object({ email: zEmail() })),
 * });
 *
 * @param zodSchema any Zod 4 schema (usually a `z.object`, but primitives work for single fields)
 */
export function toTypedSchema<TSchema extends z.ZodType>(
  zodSchema: TSchema,
): TypedSchema<z.input<TSchema>, z.output<TSchema>> {
  return {
    __type: "VVTypedSchema",
    async parse(value) {
      const result = await zodSchema.safeParseAsync(value);
      if (result.success) return { value: result.data, errors: [] };

      const errors: Record<string, TypedSchemaError> = {};
      collectIssues(result.error.issues, errors);
      return { errors: Object.values(errors) };
    },
    // No `cast()` on purpose: VeeValidate runs `initialValues`/`resetForm` values through it, and a Zod parse
    // would strip every key not declared in the schema (e.g. an `id` you still need on submit).
    describe(path) {
      // A field counts as required if `undefined` is not a valid value for it.
      const target = path ? getSchemaForPath(path, zodSchema) : zodSchema;
      if (!target) return { required: false, exists: false };
      return { required: !target.safeParse(undefined).success, exists: true };
    },
  };
}
